/**
 * 3D Product Marquee Component
 * Handles marquee animations and quick view integration
 */

class Product3DMarquee {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.pauseOnHover = container.dataset.pauseOnHover === 'true';
    this.cards = container.querySelectorAll('.marquee-3d-card');
    this.columns = container.querySelectorAll('.marquee-3d-column');

    this.init();
  }

  init() {
    this.setupQuickViewHandlers();
    this.setupAccessibility();

    // Dynamic animation speed adjustment based on content
    this.adjustAnimationSpeeds();

    // Log initialization for debugging
    if (window.Shopify && window.Shopify.designMode) {
      console.log('3D Product Marquee initialized:', {
        sectionId: this.sectionId,
        cardsCount: this.cards.length,
        columnsCount: this.columns.length
      });
    }
  }

  /**
   * Setup click handlers for quick view
   */
  setupQuickViewHandlers() {
    this.cards.forEach(card => {
      // Handle click on the entire card
      card.addEventListener('click', (e) => {
        // Don't trigger if clicking on a link
        if (e.target.closest('.marquee-card-link')) {
          return;
        }

        e.preventDefault();
        this.openQuickView(card);
      });

      // Handle click on quick view button
      const quickViewBtn = card.querySelector('.marquee-quick-view-btn');
      if (quickViewBtn) {
        quickViewBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openQuickView(card);
        });
      }
    });
  }

  /**
   * Open quick view dialog for a product card
   * @param {HTMLElement} card - The product card element
   */
  openQuickView(card) {
    const productUrl = card.dataset.productUrl;
    const productHandle = card.dataset.productHandle;

    if (!productUrl) {
      console.error('No product URL found for card:', card);
      return;
    }

    // Check if quick view dialog is available
    if (!window.dialogQuickShop || typeof window.dialogQuickShop.showModal !== 'function') {
      console.error('Quick view dialog not available. Redirecting to product page...');
      window.location.href = `/products/${productHandle}`;
      return;
    }

    // Create a temporary button element with the product URL
    // This mimics the behavior expected by the theme's quick view
    const tempButton = document.createElement('button');
    tempButton.setAttribute('data-product-url', productUrl);
    tempButton.style.display = 'none';
    document.body.appendChild(tempButton);

    try {
      // Trigger the quick view modal
      window.dialogQuickShop.showModal(tempButton);

      // Track event for analytics (if available)
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'quick_view_opened',
          product_handle: productHandle,
          source: '3d_marquee'
        });
      }
    } catch (error) {
      console.error('Error opening quick view:', error);
      // Fallback to product page
      window.location.href = `/products/${productHandle}`;
    } finally {
      // Clean up temporary button
      setTimeout(() => {
        if (tempButton.parentNode) {
          tempButton.parentNode.removeChild(tempButton);
        }
      }, 100);
    }
  }

  /**
   * Adjust animation speeds based on content height
   */
  adjustAnimationSpeeds() {
    this.columns.forEach((column) => {
      const track = column.querySelector('.marquee-3d-track');
      if (!track) return;

      const trackHeight = track.scrollHeight;
      const columnHeight = column.clientHeight;

      // Ensure smooth scrolling by adjusting speed based on content
      const baseSpeed = parseFloat(getComputedStyle(track).getPropertyValue('--marquee-duration')) || 20;
      const ratio = trackHeight / columnHeight;

      // Adjust speed slightly based on content amount
      if (ratio > 0) {
        const adjustedSpeed = baseSpeed * (ratio / 2);
        track.style.setProperty('--marquee-duration', `${adjustedSpeed}s`);
      }
    });
  }

  /**
   * Setup accessibility features
   */
  setupAccessibility() {
    // Add keyboard navigation
    this.cards.forEach(card => {
      const quickViewBtn = card.querySelector('.marquee-quick-view-btn');
      if (quickViewBtn) {
        quickViewBtn.setAttribute('tabindex', '0');

        // Handle Enter and Space keys
        quickViewBtn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.openQuickView(card);
          }
        });
      }

      // Make card keyboard accessible
      const cardLink = card.querySelector('.marquee-card-link');
      if (cardLink) {
        cardLink.setAttribute('tabindex', '0');
      }
    });

    // Announce to screen readers when hovering
    if (this.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => {
        this.container.setAttribute('aria-live', 'polite');
      });

      this.container.addEventListener('mouseleave', () => {
        this.container.removeAttribute('aria-live');
      });
    }
  }

  /**
   * Refresh the marquee (useful for theme editor)
   */
  refresh() {
    this.cards = this.container.querySelectorAll('.marquee-3d-card');
    this.columns = this.container.querySelectorAll('.marquee-3d-column');
    this.adjustAnimationSpeeds();
  }

  /**
   * Destroy instance and clean up
   */
  destroy() {
    this.cards.forEach(card => {
      const newCard = card.cloneNode(true);
      card.parentNode.replaceChild(newCard, card);
    });
  }
}

/**
 * Initialize all 3D marquee sections on the page
 */
function initProduct3DMarquees() {
  const containers = document.querySelectorAll('.product-3d-marquee-section');
  const instances = [];

  containers.forEach(container => {
    const instance = new Product3DMarquee(container);
    instances.push(instance);

    // Store instance on the element for later access
    container.marqueeInstance = instance;
  });

  return instances;
}

/**
 * Initialize on DOM ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProduct3DMarquees);
} else {
  initProduct3DMarquees();
}

/**
 * Shopify Theme Editor support
 */
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.product-3d-marquee-section');
    if (container) {
      // Destroy old instance if exists
      if (container.marqueeInstance) {
        container.marqueeInstance.destroy();
      }
      // Create new instance
      container.marqueeInstance = new Product3DMarquee(container);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target.querySelector('.product-3d-marquee-section');
    if (container && container.marqueeInstance) {
      container.marqueeInstance.destroy();
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const container = event.target.querySelector('.product-3d-marquee-section');
    if (container && container.marqueeInstance) {
      container.marqueeInstance.refresh();
    }
  });
}

/**
 * Export for use in other scripts if needed
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Product3DMarquee;
}
